import { BaseApi } from "../BaseApi";

let API: BaseApi | null = null;

function getApiInstance(): BaseApi {
  if (!API) {
    API = new BaseApi();
  }
  return API;
}

function buildUrl(
  baseUrl: string,
  params?: { [key: string]: string | number | undefined | null }
) {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      const v = params[key];
      if (v === undefined || v === null || v === "") return;
      queryParams.append(key, String(v));
    });
  }
  const qs = queryParams.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
}

// Mutations invalidate cached list/summary responses so the next fetch
// reflects the change instead of the stale cache.
const withShippingInvalidate = <T>(p: Promise<T>): Promise<T> =>
  p.then((res) => {
    BaseApi.invalidateCache("shippings");
    return res;
  });

function getShippingsSummary(params?: {
  owner?: string;
  deliveryGuy?: string;
  country?: string;
  product?: string;
  boutique?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const baseUrl = "shippings/summary";
  const clean: Record<string, string> = {};
  if (params?.owner) clean.owner = params.owner;
  if (params?.deliveryGuy) clean.deliveryGuy = params.deliveryGuy;
  if (params?.country) clean.country = params.country;
  if (params?.product) clean.product = params.product;
  if (params?.boutique) clean.boutique = params.boutique;
  if (params?.dateFrom) clean.dateFrom = params.dateFrom;
  if (params?.dateTo) clean.dateTo = params.dateTo;
  const url = Object.keys(clean).length ? buildUrl(baseUrl, clean) : baseUrl;
  return getApiInstance().getCached(url);
}

function getShippings(params: {
  limit: number;
  skip: number;
  keyword?: string;
  owner?: string;
  deliveryGuy?: string;
  country?: string;
  status?: string;
  product?: string;
  boutique?: string;
}) {
  const baseUrl = "shippings";
  const url = buildUrl(baseUrl, params);
  return getApiInstance().getCached(url);
}

// Lazy-fetch shipping history for the modal — list endpoint no longer includes
// it (heavy payload). Matches the same pattern used for orders.
function getShippingHistoryApi(shippingId: string) {
  return getApiInstance().get(
    `shippings/${encodeURIComponent(shippingId)}/history`
  );
}

function createShippingApi(params: {
  country: string;
  product: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  price: number;
  quantity: number;
  sourceLink: string;
  sourceName: string;
  totalPrice: number;
}) {
  const baseUrl = "shippings";
  const url = buildUrl(baseUrl);

  try {
    return withShippingInvalidate(getApiInstance().post(url, params));
  } catch (error) {
    console.error("Error creating Shipping:", error);
  }
}

function deleteShippingApi(params: { ids: string[] }) {
  const baseUrl = "shippings";
  const url = `${baseUrl}/delete`;
  try {
    return withShippingInvalidate(getApiInstance().put(url, params));
  } catch (error) {
    console.error("Error Deletting Shipping:", error);
  }
}

function updateShippingApi(params: {
  id: string;
  product: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  price: number;
  quantity: number;
  sourceLink: string;
  sourceName: string;
  totalPrice: number;
}) {
  const baseUrl = "shippings";
  const url = `${baseUrl}/${params.id}`;
  try {
    return withShippingInvalidate(getApiInstance().put(url, params));
  } catch (error) {
    console.error("Error updating Shipping:", error);
  }
}

function updateStatusShippingApi(params: {
  id: string;
  status: string;
  comment: string;
  /** COD amount collected — required when status = delivered */
  cashAmount?: number;
  cashCurrency?: string;
  /** Proof-of-delivery photo (data URL) — required when status = delivered */
  podPhotoUrl?: string;
  /** Signature capture (data URL) — optional */
  podSignatureUrl?: string;
  /** Person who received the parcel */
  receivedBy?: string;
  /** GPS at delivery (optional) */
  location?: { lat?: number; lng?: number };
  /** Structured failure reason — required when status = reprogrammed */
  outcome?: string;
  /** ISO datetime the delivery is postponed to — required when status = reprogrammed */
  reprogrammedFor?: string;
}) {
  const baseUrl = "shippings";
  const url = `${baseUrl}/status/${params.id}`;
  try {
    return withShippingInvalidate(getApiInstance().put(url, params));
  } catch (error) {
    console.error("Error updating status Shipping:", error);
  }
}

function updateDeliveryShippingApi(params: { id: string; delivery: string }) {
  const baseUrl = "shippings";
  const url = `${baseUrl}/delivery/${params.id}`;
  try {
    return withShippingInvalidate(getApiInstance().put(url, params));
  } catch (error) {
    console.error("Error updating delivery guy Shipping:", error);
  }
}

function getShipping(params: { id: string }) {
  const baseUrl = "shippings";
  const url = `${baseUrl}/${params.id}`;
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching Shipping:", error);
  }
}

function getShippingStatsApi(params: {
  startDate: string;
  endDate: string;
  boutique?: string;
  country?: string;
}) {
  const baseUrl = "shippings/shipping-stats";
  const clean: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v) clean[k] = String(v);
  });
  const url = buildUrl(baseUrl, clean);
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching shipping stats:", error);
    throw error;
  }
}

function getShippingDeliveryDetailApi(params: {
  id: string;
  startDate: string;
  endDate: string;
  country?: string;
}) {
  const { id, ...rest } = params;
  const url = buildUrl(`shippings/delivery-detail/${encodeURIComponent(id)}`, rest);
  return getApiInstance().get(url);
}

function getShippingDeliveryStatsApi(params: {
  startDate: string;
  endDate: string;
  country?: string;
}) {
  const baseUrl = "shippings/delivery-stats";
  const url = buildUrl(baseUrl, params as { [key: string]: string });
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching delivery stats:", error);
    throw error;
  }
}

function getShippingSlaStatsApi(params: {
  startDate: string;
  endDate: string;
  country?: string;
}) {
  const baseUrl = "shippings/sla-stats";
  const url = buildUrl(baseUrl, params as { [key: string]: string });
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching SLA stats:", error);
    throw error;
  }
}

function downloadShippingsList(params: {
  keyword?: string;
  owner?: string;
  deliveryGuy?: string;
  deliveryGuyName?: string;
  country?: string;
  status?: string;
  product?: string;
}) {
  const baseUrl = "shippings/download-delivery-list";

  const queryParams = {
    keyword: params.keyword,
    owner: params.owner,
    deliveryGuy: params.deliveryGuy,
    country: params.country,
    status: params.status,
    product: params.product,
  };

  const url = buildUrl(baseUrl, queryParams);

  let fileName = "deliveryList";

  if (params.deliveryGuyName) {
    fileName += `-${params.deliveryGuyName}`;
  }

  if (params.status) {
    fileName += `-${params.status}`;
  }

  fileName += ".xlsx";

  try {
    return getApiInstance().downloadFileForm(url, queryParams, fileName);
  } catch (error) {
    console.error("Error downloading shippings list:", error);
  }
}

function getShippingTransitions(id: string) {
  try {
    return getApiInstance().get(`shippings/${id}/transitions`);
  } catch (error) {
    console.error("Error fetching shipping transitions:", error);
    throw error;
  }
}

export {
  getShippings,
  getShippingsSummary,
  createShippingApi,
  getShipping,
  updateShippingApi,
  deleteShippingApi,
  updateStatusShippingApi,
  updateDeliveryShippingApi,
  downloadShippingsList,
  getShippingStatsApi,
  getShippingDeliveryStatsApi,
  getShippingDeliveryDetailApi,
  getShippingSlaStatsApi,
  getShippingTransitions,
  getShippingHistoryApi,
};
